/*
    Copyright 2020 Lauren Gyldenlove.
    This file is part of Duelmaster.

    Duelmaster is free software: you can redistribute it and/or modify
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Duelmaster is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with Foobar.  If not, see <https://www.gnu.org/licenses/>.
 */

import React, { useState, ChangeEvent, MutableRefObject } from 'react';
import * as Player from '../model/Player';
import * as Ledger from '../model/Ledger';
import { AppContext } from '../App';
import { InputBase, IconButton, SvgIcon, Paper, Tooltip } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { mdiFire } from '@mdi/js';
import { mdiHospital } from '@mdi/js';

const useStyles = makeStyles((theme: Theme) => { 
    return {
        root: {
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: "#000"
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1
        },
        effectCalculator: {
            padding: "1em",
            textAlign: "center"
        }
    }
});

export interface EffectCalculatorFieldProps {
    playerPos: Player.Position;
    playerLedger: Ledger.Model;
    setLedger: Function;
    focusRef: MutableRefObject<HTMLInputElement | undefined>;
}

export const EffectCalculatorField = (props: EffectCalculatorFieldProps) => {
    const [effectDamageInput, setEffectDamageInput] = useState<string>("");
    const classes = useStyles();

    return (
        <AppContext.Consumer>
            {(context) => {
                function applyDamageEffect(): void {
                    const effectDamage = Number(effectDamageInput) || 0;
                    if(effectDamage > 0) {
                        props.setLedger(Ledger.addEffectDamage(props.playerLedger, effectDamage));
                        setEffectDamageInput("");
                    }
                }

                function applyHealingEffect(): void {
                    const effectDamage = Number(effectDamageInput) || 0;
                    if(effectDamage > 0) {
                        props.setLedger(Ledger.addEffectDamage(props.playerLedger, effectDamage * -1));
                        setEffectDamageInput("");
                    }
                }

                function updateEffectDamage(event: ChangeEvent<HTMLInputElement>): void {
                    setEffectDamageInput(event.currentTarget.value);
                }

                const playerIsLeft = props.playerPos === Player.Position.LEFT;
                const playerName = playerIsLeft ? context.leftPlayer.name : context.rightPlayer.name;
                
                return (
                    <React.Fragment>
                        <Paper className={classes.root}>
                            <InputBase inputRef={props.focusRef} className={classes.input} id={"effectDamage-"+ playerName} placeholder={"Apply Against " + playerName} value={effectDamageInput} onChange={updateEffectDamage} />
                            <Tooltip title="Damage player">
                                <IconButton color="secondary" onClick={applyDamageEffect}>
                                    <SvgIcon fontSize="large">
                                        <path d={mdiFire} />
                                    </SvgIcon>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Heal player">
                                <IconButton color="primary" onClick={applyHealingEffect}>
                                    <SvgIcon fontSize="large">
                                        <path d={mdiHospital} />
                                    </SvgIcon>
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    </React.Fragment>
                );
            }}
        </AppContext.Consumer>
    );
};
